/**
 * ImportCsvSection — Sprint 13 S13-16.
 *
 * Componente reutilizable para mantenedores de sa (lotes, personas) que
 * permite carga masiva desde CSV. Muestra el formato esperado, ejemplo
 * de fila, boton "Descargar plantilla" y selector de archivo. Tras subir,
 * muestra resumen del backend (total/creados/errores por fila).
 */
import { useRef, useState, type ChangeEvent } from 'react';
import { ApiError } from '../lib/api/client';
import { Button } from './ui/Button';

export interface CsvColumna {
  nombre: string;
  obligatoria: boolean;
  ejemplo: string;
  notas?: string;
}

export interface CsvImportResultadoErrorFila {
  fila: number;
  error: string;
}

export interface CsvImportResultado {
  total_filas: number;
  creados: number;
  errores: CsvImportResultadoErrorFila[];
}

interface ImportCsvSectionProps {
  titulo: string;
  descripcion: string;
  columnas: CsvColumna[];
  nombrePlantilla: string;
  subir: (archivo: File) => Promise<CsvImportResultado>;
  onResultado?: (r: CsvImportResultado) => void;
}

export function ImportCsvSection({
  titulo,
  descripcion,
  columnas,
  nombrePlantilla,
  subir,
  onResultado,
}: ImportCsvSectionProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resultado, setResultado] = useState<CsvImportResultado | null>(null);

  function descargarPlantilla() {
    const header = columnas.map((c) => c.nombre).join(',');
    const ejemplo = columnas.map((c) => c.ejemplo).join(',');
    const contenido = `${header}\n${ejemplo}\n`;
    const blob = new Blob([contenido], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = nombrePlantilla;
    a.click();
    URL.revokeObjectURL(url);
  }

  async function handleArchivo(e: ChangeEvent<HTMLInputElement>) {
    const archivo = e.target.files?.[0];
    if (!archivo) return;
    setSubmitting(true);
    setError(null);
    setResultado(null);
    try {
      const r = await subir(archivo);
      setResultado(r);
      if (onResultado) onResultado(r);
    } catch (err) {
      // Sprint 13 patch: detectar ApiError robusto. `instanceof` puede
      // fallar entre chunks de Vite cuando el modulo se carga 2 veces,
      // asi que tambien chequeamos por shape.
      const apiLike =
        err instanceof ApiError ||
        (typeof err === 'object' &&
          err !== null &&
          'status' in err &&
          'body' in err);
      if (apiLike) {
        const e = err as { status?: number; body?: unknown };
        const detail =
          typeof e.body === 'object' && e.body !== null && 'detail' in e.body
            ? String((e.body as { detail: unknown }).detail)
            : typeof e.body === 'string'
              ? e.body
              : `Error ${e.status ?? 'desconocido'}`;
        setError(detail);
      } else if (err instanceof Error) {
        setError(`Error al subir el archivo: ${err.message}`);
      } else {
        setError('Error desconocido al subir el archivo');
      }
    } finally {
      setSubmitting(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  }

  return (
    <details
      className="bg-bg-card border-[0.5px] border-white/[0.08] rounded-md"
      data-testid="import-csv-section"
    >
      <summary className="cursor-pointer px-4 py-3 text-sm font-medium text-text-primary hover:bg-bg-sunken transition-colors">
        ↑ {titulo}
      </summary>
      <div className="px-4 py-4 space-y-4 border-t-[0.5px] border-white/[0.08]">
        <p className="text-sm text-text-secondary max-w-3xl">{descripcion}</p>

        <div>
          <div className="text-xs uppercase tracking-wide text-text-secondary font-medium mb-2">
            Formato esperado (UTF-8, separador coma, primera fila = header)
          </div>
          <div className="bg-bg-sunken rounded p-3 text-sm overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="text-text-secondary text-xs uppercase tracking-wide">
                  <th className="pb-2 pr-4">Columna</th>
                  <th className="pb-2 pr-4">Obligatoria</th>
                  <th className="pb-2 pr-4">Ejemplo</th>
                  <th className="pb-2">Notas</th>
                </tr>
              </thead>
              <tbody>
                {columnas.map((c) => (
                  <tr
                    key={c.nombre}
                    className="border-t-[0.5px] border-white/[0.06]"
                  >
                    <td className="py-1.5 pr-4 font-mono text-text-primary">
                      {c.nombre}
                    </td>
                    <td className="py-1.5 pr-4 text-text-secondary">
                      {c.obligatoria ? 'sí' : 'no'}
                    </td>
                    <td className="py-1.5 pr-4 font-mono text-text-secondary">
                      {c.ejemplo}
                    </td>
                    <td className="py-1.5 text-xs text-text-muted">
                      {c.notas ?? ''}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="text-xs text-text-muted mt-2">
            Orden de columnas exigido. Las opcionales pueden estar vacías
            pero la columna debe existir en el header.
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <Button variant="ghost" type="button" onClick={descargarPlantilla}>
            Descargar plantilla CSV
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv"
            onChange={handleArchivo}
            disabled={submitting}
            className="text-sm text-text-secondary file:mr-3 file:rounded file:border file:border-white/15 file:bg-bg-sunken file:px-3 file:py-1.5 file:text-text-primary file:hover:bg-bg-card file:cursor-pointer"
            aria-label="Subir CSV"
          />
          {submitting && (
            <span className="text-xs text-text-muted">Procesando…</span>
          )}
        </div>

        {error && (
          <div
            className="bg-state-danger/15 border-l-4 border-state-danger p-3 rounded-sm text-sm text-text-primary"
            role="alert"
          >
            {error}
          </div>
        )}

        {resultado && (
          <div
            className="bg-state-success/15 border-l-4 border-state-success p-3 rounded-sm text-sm text-text-primary space-y-2"
            role="status"
          >
            <div>
              <span className="font-medium">
                {resultado.creados} de {resultado.total_filas}
              </span>{' '}
              filas importadas correctamente.
              {resultado.errores.length > 0 && (
                <span className="text-text-muted">
                  {' · '}
                  {resultado.errores.length}{' '}
                  {resultado.errores.length === 1 ? 'error' : 'errores'}.
                </span>
              )}
            </div>
            {resultado.errores.length > 0 && (
              <ul className="text-xs text-text-secondary space-y-1 list-disc list-inside max-h-40 overflow-y-auto">
                {resultado.errores.map((e) => (
                  <li key={e.fila}>
                    Fila {e.fila}: {e.error}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>
    </details>
  );
}
